import { createTheme } from '@mui/material';
import { hslToRgb, rgbToHex } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import components from './components';

export const pastelColors = [
  '#FFD6A5', // Light Orange
  '#CAFFBF', // Light Green
  '#A0C4FF', // Light Blue
  '#BDB2FF', // Light Indigo
  '#FFC6FF', // Light Magenta
  '#FFB7B2', // Pastel Red
  '#FFDAC1', // Pastel Orange
  '#E2F0CB', // Pastel Yellow-Green
  '#B5EAD7', // Pastel Green
  '#cbc7fc',
  '#FFADAD', // Light Red
  '#fec8a7',
  '#C7CEEA', // Pastel Blue-Gray
  '#bae6fd', // Sky Blue
  '#e0bBE4',
  '#a2d2ff',
  '#f2d2BD',
  '#99ddc8',
  '#fec8d8',
  '#fcf4dd',
  '#b5eecb',
  '#ffefd5',
  '#ffac81',
  '#b0e0e6',
  '#fddde6',
  '#f0f8ff',
  '#fae3d9',
  '#d1d9e6',
  '#fde2e4',
  '#cddafd',
];

function hexToHSL(hex, mode = 'light') {
  if (!hex) return {};
  let r = 0;
  let g = 0;
  let b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }

  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  let h = 0;
  let s = 0;
  let l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        break;
    }
    h /= 6;
  }

  // If dark mode, reduce the lightness of the color.
  if (mode === 'dark') {
    l = Math.max(0, l * 0.45); // Make it significantly darker
    s = Math.max(0, s * 0.8); // Slightly desaturate
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

const generateDarkerColor = (hex) => {
  const hsl = hexToHSL(hex);
  // Reduce lightness by 25% and saturation by 10%
  const darkHsl = {
    h: hsl.h,
    s: Math.max(0, hsl.s * 0.9), // Ensure saturation doesn't go below 0
    l: Math.max(0, hsl.l * 0.75), // Ensure lightness doesn't go below 0
  };
  return rgbToHex(hslToRgb(`hsl(${darkHsl.h}, ${darkHsl.s}%, ${darkHsl.l}%)`));
};

export const darkPastelColors = pastelColors.map((hex) =>
  generateDarkerColor(hex)
);

const bgAndColorStyle = (hexColor, currentTheme = 'light') => {
  const hslColor = hexToHSL(hexColor, currentTheme);

  return {
    '--hue': hslColor.h,
    '--saturation': `${hslColor.s}%`,
    backgroundColor: `hsl(var(--hue), var(--saturation), ${currentTheme === 'light' ? 92 : 70}%)`,
    color: `hsl(var(--hue), var(--saturation), ${currentTheme === 'light' ? 25 : 15}%)`,
  };
};

// --- Color Utility Generator ---
// This function creates the color utilities based on the provided color array.
const createColorUtilities = (colors) => {
  const stringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str?.length; i += 1) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash % colors.length);
    return colors[index];
  };

  const generateConsistentUniqueColors = (
    strings,
    baseColors = pastelColors
  ) => {
    const colorMap = {};
    const sortedStrings = [...new Set(strings)].sort();

    sortedStrings.forEach((str, index) => {
      const lightColor = baseColors[index % baseColors.length];
      const darkColor = generateDarkerColor(lightColor);
      colorMap[str] = {
        light: lightColor,
        dark: darkColor,
      };
    });

    return strings.map((str) => colorMap[str]);
  };

  return {
    stringToColor,
    generateConsistentUniqueColors,
  };
};

const baseTheme = {
  breakpoints: {
    // Explicit values so the historical 1300px desktop cutoff lives in the
    // theme (lg) and the mobile/tablet tiers are first-class. sm/md keep their
    // MUI defaults, so existing `breakpoints.up('sm'|'md')` usages don't shift.
    values: { xs: 0, sm: 600, md: 900, lg: 1300, xl: 1536 },
  },
  custom: {
    bgAndColorStyle,
    // The brand gradient and the greyscale it becomes when the action it marks
    // is unavailable. `brand` is the canonical form; the mobile header's own
    // angle is under `mobile.brandGradient`.
    gradients: {
      brand: 'linear-gradient(90.37deg, #1D5A9D 21.99%, #61B340 100%)',
      brandDisabled: 'linear-gradient(90.37deg, #5A5A5A 21.99%, #BCBCBC 100%)',
    },
    // The tinted square an icon sits in, on a tile or a row.
    iconTile: {
      size: 90,
      borderRadius: 4,
    },
    // Mobile design tokens (see MOBILE_PLAN.md §B). Consumed by
    // src/Components/Mobile/*. Kept under `custom` so they travel with the
    // theme like bgAndColorStyle.
    mobile: {
      tabBarHeight: 78,
      headerRadius: '0 0 22px 22px',
      sheetRadius: '24px 24px 0 0',
      cardRadius: 16,
      mutedColor: '#94a3b8',
      activePill: '#e8f0fa',
      brandGradient: 'linear-gradient(90deg, #1D5A9D, #61B340)',
      phoneMaxWidth: 480,
    },
  },
  font: {
    primary: 'Roboto, Inter, sans-serif',
  },
  components,
};

const sharedPalette = {
  palette: {
    white: {
      main: '#ffffff',
    },
    greys: {
      main: '#000000',
      secondary: '#64748B',
      lighter: '#BCBCBC',
      darker: '#5A5A5A',
      graph: '#e3e8f1',
      disableBtn: '#b3b3b3',
    },
    graphs: {
      green: '#6CDA95',
      answered: 'rgba(34, 197, 94, 0.6)',
      notAnswered: 'rgba(205, 0, 0, 0.6)',
      curveLine: '#8D4EDA',
      curveArea: '#C396ED',
      purple: '#8474bc',
    },
  },
};

const themes = {
  light: deepmerge(
    { ...sharedPalette, ...baseTheme },
    {
      custom: createColorUtilities(pastelColors), // Use light pastel colors
      palette: {
        primary: {
          main: '#1D5B9D',
        },
        divider: 'rgba(0, 0, 0, 0.12)',
        mode: 'light',
        background: { default: '#F0F2F5', paper: '#ffffff' },
      },
    }
  ),
  dark: deepmerge(
    { ...sharedPalette, ...baseTheme },
    {
      custom: {
        ...createColorUtilities(darkPastelColors), // Use dark pastel colors
        // In dark mode the light-blue selected pill reads as a jarring white
        // block; use a subtle translucent-primary highlight instead.
        mobile: { activePill: 'rgba(88, 147, 212, 0.22)' },
      },
      palette: {
        primary: {
          main: '#5893D4',
        },
        divider: 'rgba(255, 255, 255, 0.12)',
        mode: 'dark',
        background: { default: '#111111', paper: '#171717' },
      },
    }
  ),
};

export default (mode) => createTheme(themes[mode]);
